// src/components/Sidebar/SidebarMenu.jsx
import React, { useMemo, useState } from "react";
import {
  List, ListItem, ListItemButton, ListItemText, Collapse, Box,
  Typography, Divider, Chip, ListSubheader, Badge,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";

import LogoutButton from "../Common/LogoutButton";
import { useCategory } from "../../contexts/CategoryContext";
import { useUser } from "../../contexts/UserContext";
import api from "../../api/axios";

import useEventActive from "./hooks/useEventActive";
import useLastBoardCategory from "./hooks/useLastBoardCategory";
import useUnreadChats from "./hooks/useUnreadChats";

import NewYearEventCard from "./components/NewYearEventCard";
import TreeEventCard from "./components/TreeEventCard";
import BannerBlock from "./components/BannerBlock";

export default function SidebarMenu({ onItemClick = () => { } }) {
  const { boardGroups = [], chatCategories = [] } = useCategory();
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const query = location.search;

  // ─────────────────────────────────────────
  // 공통 스타일 룰
  // ─────────────────────────────────────────
  const itemSx = {
    borderRadius: 2,
    mx: 1,
    my: 0.25,
    px: 1.25,
    "&:hover": { backgroundColor: "action.hover" },
  };

  const selectedSx = {
    backgroundColor: "action.selected",
    boxShadow: "inset 3px 0 0 0 rgba(25, 118, 210, 0.9)",
    "&:hover": { backgroundColor: "action.selected" },
  };

  const sectionHeaderSx = {
    px: 2,
    pt: 1.25,
    pb: 0.75,
    lineHeight: 1,
    fontSize: 12,
    letterSpacing: "0.02em",
    color: "text.secondary",
    fontWeight: 800,
    textTransform: "uppercase",
  };

  const primaryTextProps = (active) => ({
    fontWeight: active ? 800 : 500,
    fontSize: 14,
    lineHeight: 1.2,
  });

  const isMatch = (prefix) => path.startsWith(prefix);
  const go = (url) => {
    navigate(url);
    onItemClick();
  };

  // ─────────────────────────────────────────
  // 상태: 마지막 카테고리 / 이벤트 활성 / unread
  // ─────────────────────────────────────────
  const { lastCat, setLastCat } = useLastBoardCategory(location.search);

  const treeEventActive = useEventActive("/api/events/tree/active/");
  const newYearEventActive = useEventActive("/api/events/newyear/active/");

  const activeChatId = Number(path.match(/\/main\/chat\/category\/(\d+)/)?.[1] || 0);

  const {
    unreadChatIds,
    unreadOtherExists,
    unreadCount,
    clearUnread,
  } = useUnreadChats({ loggedIn: !!userInfo?.logged_in, activeChatId });

  // ─────────────────────────────────────────
  // 아코디언 오픈 상태
  // ─────────────────────────────────────────
  const [openChat, setOpenChat] = useState(path.startsWith("/main/chat"));
  const [openMarket, setOpenMarket] = useState(
    path.startsWith("/main/gacha") || path.startsWith("/main/store")
  );
  const [openGroup, setOpenGroup] = useState({});
  const toggleGroup = (gid) => setOpenGroup((prev) => ({ ...prev, [gid]: !prev[gid] }));

  // ─────────────────────────────────────────
  // 핸들러
  // ─────────────────────────────────────────
  const handleBoardClick = (id) => {
    setLastCat(id);
    go(`/main/board?category=${id}`);
  };

  const handleChatClick = (id) => {
    clearUnread(id);
    go(`/main/chat/category/${id}`);
  };

  const handleTopicMafiaQuickEnter = async () => {
    try {
      const res = await api.post("/api/topic-mafia/quick-enter/", {});
      const roomId = res.data?.room_id;
      if (!roomId) return;

      go(`/main/games/topic-mafia/${roomId}`);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || "라이어 게임 입장 실패");
    }
  };

  // 게시판 카테고리 selected 판단 (원본 로직 최대 유지)
  const isBoardCategoryActive = (categoryId) => {
    const q = new URLSearchParams(location.search).get("category");
    if (q) return String(categoryId) === String(q);
    return String(categoryId) === String(lastCat);
  };

  return (
    <>
      <List sx={{ pt: 0.5 }} subheader={<li />}>
        {/* ===== 정보 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          정보
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/main/jokbo"
            onClick={onItemClick}
            selected={isMatch("/main/jokbo")}
            sx={{ ...itemSx, ...(isMatch("/main/jokbo") ? selectedSx : {}) }}
          >
            <ListItemText primary="📜 족보" primaryTypographyProps={primaryTextProps(isMatch("/main/jokbo"))} />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/main/calendar"
            onClick={onItemClick}
            selected={isMatch("/main/calendar")}
            sx={{ ...itemSx, ...(isMatch("/main/calendar") ? selectedSx : {}) }}
          >
            <ListItemText primary="📅 일정" primaryTypographyProps={primaryTextProps(isMatch("/main/calendar"))} />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1.25, opacity: 0.6 }} />

        {/* ===== 게시판 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          게시판
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/main/board"
            onClick={onItemClick}
            selected={path === "/main/board" && !query}
            sx={{ ...itemSx, ...((path === "/main/board" && !query) ? selectedSx : {}) }}
          >
            <ListItemText
              primary="📑 전체 게시글"
              primaryTypographyProps={primaryTextProps(path === "/main/board" && !query)}
            />
          </ListItemButton>
        </ListItem>

        {/* 게시판 그룹 */}
        {boardGroups.map((g) => {
          const open = !!openGroup[g.id];
          return (
            <Box key={g.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => toggleGroup(g.id)} sx={{ ...itemSx, mx: 1, my: 0.25 }}>
                  <ListItemText
                    primary={`${g.emoji || "📋"} ${g.name}`}
                    primaryTypographyProps={primaryTextProps(open)}
                  />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>

              <Collapse in={open} timeout="auto" unmountOnExit>
                {g.categories.map((c) => {
                  const isActive = isBoardCategoryActive(c.id);
                  return (
                    <ListItem key={c.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleBoardClick(c.id)}
                        selected={isActive}
                        sx={{
                          ...itemSx,
                          ...(isActive ? selectedSx : {}),
                          ml: 2.5,
                          mr: 1,
                        }}
                      >
                        <ListItemText
                          primary={`${c.emoji || "📁"} ${c.name}`}
                          primaryTypographyProps={{
                            ...primaryTextProps(isActive),
                            noWrap: true,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}

        <Divider sx={{ my: 1.25, opacity: 0.6 }} />

        {/* ===== 채팅 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          채팅
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenChat(!openChat)} sx={{ ...itemSx }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      component="span"
                      sx={{
                        ...primaryTextProps(openChat),
                        color: unreadOtherExists ? "error.main" : "inherit",
                      }}
                    >
                      🗣️ 채팅방
                    </Typography>

                    {unreadOtherExists && (
                      <Chip
                        size="small"
                        label={unreadCount > 99 ? "99+" : unreadCount}
                        color="error"
                        sx={{ height: 20, fontWeight: 800 }}
                      />
                    )}
                  </Box>
                }
              />
            </Box>
            {openChat ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={openChat} timeout="auto" unmountOnExit>
          {chatCategories.map((cat) => {
            const isActive = activeChatId === cat.id;
            const hasUnread = unreadChatIds.includes(cat.id) && !isActive;

            return (
              <ListItem key={cat.id} disablePadding>
                <ListItemButton
                  onClick={() => handleChatClick(cat.id)}
                  selected={isActive}
                  sx={{
                    ...itemSx,
                    ...(isActive ? selectedSx : {}),
                    ml: 2.5,
                    mr: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <ListItemText
                      primary={`${cat.emoji || "💬"} ${cat.name}`}
                      primaryTypographyProps={{
                        ...primaryTextProps(isActive),
                        color: hasUnread ? "error.main" : "inherit",
                        noWrap: true,
                      }}
                    />
                    {hasUnread && (
                      <Badge
                        variant="dot"
                        color="error"
                        sx={{
                          ml: 1,
                          "& .MuiBadge-badge": { right: 0, top: 10 },
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })}
        </Collapse>

        <Divider sx={{ my: 1.25, opacity: 0.6 }} />

        {/* ===== 마켓 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          마켓
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton onClick={() => setOpenMarket(!openMarket)} sx={{ ...itemSx }}>
            <ListItemText primary="🏪 산들마켓" primaryTypographyProps={primaryTextProps(openMarket)} />
            {openMarket ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={openMarket} timeout="auto" unmountOnExit>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/main/gacha/title"
              onClick={onItemClick}
              selected={isMatch("/main/gacha/title")}
              sx={{ ...itemSx, ...(isMatch("/main/gacha/title") ? selectedSx : {}), ml: 2.5, mr: 1 }}
            >
              <ListItemText primary="🎖️ 칭호뽑기" primaryTypographyProps={primaryTextProps(isMatch("/main/gacha/title"))} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/main/gacha/luckybox"
              onClick={onItemClick}
              selected={isMatch("/main/gacha/luckybox")}
              sx={{ ...itemSx, ...(isMatch("/main/gacha/luckybox") ? selectedSx : {}), ml: 2.5, mr: 1 }}
            >
              <ListItemText
                primary="🧧 칭호 럭키박스"
                primaryTypographyProps={primaryTextProps(isMatch("/main/gacha/luckybox"))}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/main/store"
              onClick={onItemClick}
              selected={isMatch("/main/store")}
              sx={{ ...itemSx, ...(isMatch("/main/store") ? selectedSx : {}), ml: 2.5, mr: 1 }}
            >
              <ListItemText primary="🛒 아이템 상점" primaryTypographyProps={primaryTextProps(isMatch("/main/store"))} />
            </ListItemButton>
          </ListItem>
        </Collapse>

        <Divider sx={{ my: 1.25, opacity: 0.6 }} />

        {/* ===== 게임 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          게임
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            onClick={handleTopicMafiaQuickEnter}
            selected={isMatch("/main/games/topic-mafia")}
            sx={{ ...itemSx, ...(isMatch("/main/games/topic-mafia") ? selectedSx : {}) }}
          >
            <ListItemText primary="🎭 라이어 게임" primaryTypographyProps={primaryTextProps(isMatch("/main/games/topic-mafia"))} />
          </ListItemButton>
        </ListItem>

        {/* ===== 계정 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          계정
        </ListSubheader>

        <LogoutButton />

        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href="https://glossy-thief-ee9.notion.site/2354d005ad0780a6aceee0bd2e9d3e50?source=copy_link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onItemClick}
            aria-label="운영정책(새 창)"
            sx={{ ...itemSx }}
          >
            <ListItemText primary="📄 운영정책" primaryTypographyProps={primaryTextProps(false)} />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1.25, opacity: 0.6 }} />

        {/* ===== 이벤트 ===== */}
        <ListSubheader disableSticky sx={sectionHeaderSx}>
          이벤트
        </ListSubheader>

        {newYearEventActive && (
          <NewYearEventCard
            onClick={() => {
              go("/main/events/newyear");
            }}
          />
        )}

        {treeEventActive && (
          <TreeEventCard
            onClick={() => {
              go("/main/events/tree");
            }}
          />
        )}

        <BannerBlock onItemClick={onItemClick} />
      </List>
    </>
  );
}
